import { ExternalLink } from "lucide-react";
import { Pastilles, accorder, compterPar } from "./LignesImportEvalNat";

/**
 * Le résultat de l'import des résultats nationaux (plan du 30/10/2026, étape 3), une ligne par classe et matière
 * envoyée, tel que le serveur l'a fait. Les élèves introuvables sont nommés une fois, puis les deux cas, une ligne
 * chacun : un nom écrit autrement se corrige dans les fichiers (n° 31 du diagnostic du 31/10/2026), un élève absent
 * de TanoEd passe par l'import des élèves (D1 : la seule porte pour créer un élève). Un élève qui a quitté
 * l'établissement (sa seule fiche est désactivée, n° 33) est dit à part, en gris : rien à faire. Porte allégée
 * (31/10/2026) : l'import des élèves s'ouvre dans un nouvel onglet, et « Revenir aux fichiers » (dès qu'il y a des
 * introuvables) ramène aux fichiers déposés, que la fenêtre revérifie. L'envoi part niveau par niveau (choix 2 validé) : un niveau
 * qui échoue est dit, les autres sont importés, et les fichiers restent dans la fenêtre pour relancer.
 *   - resultat : `{ lignes, erreurs }`, les lignes de lireResultat (utils/importEvalNat) mises bout à bout et
 *     `erreurs` : `[{ niveau, message }]` ;
 *   - nonEnvoyees : `[{ nom, raison }]`, les classes encore sans résultats qui n'ont pas été envoyées (une classe vide
 *     une fois pour ses deux matières) ;
 *   - horsTanoEd : les classes des fichiers absentes de TanoEd, laissées de côté, dites en une ligne (01/11/2026).
 */

const pastillesDuResultat = (ligne) => {
    if (ligne.statut === "error") return [{ texte: ligne.message || "rien d'importé", ton: "grave" }];
    const pastilles = [];
    if (ligne.crees) pastilles.push({ texte: accorder(ligne.crees, "créé", "créés"), ton: "ok" });
    if (ligne.confirmes) pastilles.push({ texte: `dont ${accorder(ligne.confirmes, "confirmé", "confirmés")}`, ton: "ok" });
    if (ligne.completes) pastilles.push({ texte: accorder(ligne.completes, "complété", "complétés"), ton: "ok" });
    if (ligne.dejaImportes) pastilles.push({ texte: accorder(ligne.dejaImportes, "déjà importé", "déjà importés"), ton: "info" });
    compterPar(ligne.affichesAilleurs, "classe").forEach((n, classe) => {
        pastilles.push({ texte: `${accorder(n, "affiché", "affichés")} en ${classe}`, ton: "info" });
    });
    if (ligne.inconnus.length) pastilles.push({ texte: accorder(ligne.inconnus.length, "inconnu", "inconnus"), ton: "grave" });
    if (ligne.ambigus.length) pastilles.push({ texte: accorder(ligne.ambigus.length, "non rangé", "non rangés"), ton: "ambre" });
    return pastilles.length > 0 ? pastilles : [{ texte: "rien à importer", ton: "rien" }];
};

const BOUTON = "px-4 py-2 rounded-xl text-sm font-bold transition-colors";

/** Les élèves d'un champ (inconnus, ambigus, partis) avec la classe de leur fichier, une fois chacun pour ses deux matières. */
const elevesUniques = (lignes, champ) => {
    const vus = new Set();
    return lignes.flatMap((ligne) => (ligne[champ] || []).map((eleve) => ({ ...eleve, classe: ligne.classe })))
        .filter((eleve) => {
            const cle = `${eleve.nom}|${eleve.prenom}|${eleve.classe}`;
            if (vus.has(cle)) return false;
            vus.add(cle);
            return true;
        });
};

function ResultatImportEvalNat({ resultat, nonEnvoyees = [], horsTanoEd = [], onOuvrirImportEleves, onRevenir, onFermer }) {
    const { lignes, erreurs } = resultat;
    const total = (champ) => lignes.reduce((n, ligne) => n + (Array.isArray(ligne[champ]) ? ligne[champ].length : ligne[champ] || 0), 0);
    const inconnus = elevesUniques(lignes, "inconnus");
    const ambigus = elevesUniques(lignes, "ambigus");
    const partis = elevesUniques(lignes, "partis");
    // Le bilan compte comme les lignes (un élève inconnu dans ses deux matières y compte deux fois) ; les encadrés
    // nomment chaque élève une fois.
    const bilan = [
        total("crees") ? accorder(total("crees"), "créé", "créés") : null,
        total("completes") ? accorder(total("completes"), "complété", "complétés") : null,
        total("dejaImportes") ? accorder(total("dejaImportes"), "déjà importé", "déjà importés") : null,
        total("inconnus") ? accorder(total("inconnus"), "inconnu", "inconnus") : null,
        total("ambigus") ? accorder(total("ambigus"), "non rangé", "non rangés") : null,
    ].filter(Boolean).join(" · ");
    const enPartie = erreurs.length > 0;
    // Revenir aux fichiers : pour relancer un niveau en échec, ou pour importer les introuvables une fois ajoutés.
    const revenir = enPartie || inconnus.length > 0;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
                <strong className={`text-lg ${enPartie ? "text-amber-700" : "text-emerald-700"}`}>
                    {enPartie ? "Import terminé en partie" : "Import terminé"}
                </strong>
                {bilan ? <span className="text-sm text-slate-500">{bilan}</span> : null}
            </div>

            {erreurs.map(({ niveau, message }) => (
                <p key={niveau} role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm font-semibold text-red-700">
                    L'import de la {niveau}e n'a pas abouti : {message}
                </p>
            ))}

            {lignes.length > 0 ? (
                <div role="table" aria-label="Résultat par classe et matière" className="border border-slate-200 rounded-xl overflow-hidden">
                    {lignes.map((ligne) => (
                        <div key={`${ligne.classe}|${ligne.matiere}`} role="row" className="grid grid-cols-[9rem_1fr] gap-3 items-center px-3 py-2 border-t first:border-t-0 border-slate-100 text-sm">
                            <span role="cell" className="font-bold text-slate-800">
                                {ligne.classe} <span className="font-semibold text-slate-500">{ligne.matiere}</span>
                            </span>
                            <span role="cell"><Pastilles pastilles={pastillesDuResultat(ligne)} /></span>
                        </div>
                    ))}
                </div>
            ) : null}

            {nonEnvoyees.length > 0 ? (
                <ul className="flex flex-col gap-1 text-sm text-amber-700">
                    {nonEnvoyees.map(({ nom, raison }) => (
                        <li key={nom}>Rien d'importé pour la {nom} : {raison}.</li>
                    ))}
                </ul>
            ) : null}

            {horsTanoEd.length > 0 ? (
                <p className="text-sm text-slate-500">
                    {horsTanoEd.length > 1 ? "Laissées" : "Laissée"} de côté, pas dans TanoEd : {horsTanoEd.join(", ")}.
                </p>
            ) : null}

            {inconnus.length > 0 ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 flex flex-col gap-2 text-sm text-red-800">
                    <div className="flex flex-col gap-0.5">
                        <b>{accorder(inconnus.length, "élève introuvable", "élèves introuvables")} dans TanoEd</b>
                        <span>{inconnus.map((e) => `${e.nom} ${e.prenom} (${e.classe})`).join(" · ")}</span>
                    </div>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                        <dt className="font-semibold">Nom écrit autrement dans TanoEd ?</dt>
                        <dd>Corrigez-le dans les fichiers, puis redéposez-les.</dd>
                        <dt className="font-semibold">Pas encore dans TanoEd ?</dt>
                        <dd>Passez par l'import des élèves, puis revenez aux fichiers.</dd>
                    </dl>
                    <div>
                        <button type="button" onClick={onOuvrirImportEleves} className={`${BOUTON} inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-700 hover:bg-red-100`}>
                            Ouvrir l'import des élèves
                            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            ) : null}

            {ambigus.length > 0 ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                    Plusieurs fiches portent ce nom, rien n'a été rangé :{" "}
                    {ambigus.map((e) => `${e.nom} ${e.prenom} (fichier de la ${e.classe} ; fiches en ${e.candidats.map((c) => c.classe_nom).join(", ")})`).join(", ")}.
                    {" "}Redéposez les fichiers et désignez la bonne fiche à la question « laquelle ? ».
                </div>
            ) : null}

            {partis.length > 0 ? (
                <p className="text-sm text-slate-500">
                    {partis.length > 1 ? "Ont quitté l'établissement" : "A quitté l'établissement"}, rien à importer : {partis.map((e) => `${e.nom} ${e.prenom} (${e.classe})`).join(" · ")}.
                </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
                <span className="text-sm text-slate-500">
                    {revenir ? "Vos fichiers sont restés dans la fenêtre : ce qui est déjà importé sortira « déjà importé »." : null}
                </span>
                <div className="flex gap-2">
                    {revenir ? (
                        <button type="button" onClick={onRevenir} className={`${BOUTON} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
                            Revenir aux fichiers
                        </button>
                    ) : null}
                    <button type="button" onClick={onFermer} className={`${BOUTON} bg-blue-900 text-white hover:bg-blue-800`}>
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ResultatImportEvalNat;
